import { ReactNode, useCallback, useMemo, useState } from 'react'
import { queryClient } from 'queryClient'
import { PRIVATE_ROUTES } from 'routes'
import { AuthUser, RoutesMap } from 'ts/types'
import { filterRoutesByRole } from 'ts/utils'
import { AuthContext } from './AuthContext'

const STORAGE_KEY = 'authUser'

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<AuthUser | null>(() => {
    const storedUser = localStorage.getItem(STORAGE_KEY)
    return storedUser ? JSON.parse(storedUser) : null
  })

  const saveUser = useCallback((userData: AuthUser) => {
    setUser(userData)
    localStorage.setItem(STORAGE_KEY, JSON.stringify(userData))
  }, [])

  const clearUser = useCallback(() => {
    setUser(null)
    localStorage.removeItem(STORAGE_KEY)
    localStorage.removeItem('selectedEstimate')
    localStorage.removeItem('initialFilters')
    // Clear cached per-user query data so next login doesn't see stale data
    queryClient.clear()
  }, [])

  const isLoggedIn = useCallback(() => {
    const currentTime = Date.now()
    const expirationTime = user?.expirationTime || 0
    return currentTime < expirationTime
  }, [user])

  const allowedRoutes = useMemo(() => {
    if (user && user.role) {
      return filterRoutesByRole(PRIVATE_ROUTES, user.role)
    }
    return {} as RoutesMap
  }, [user])

  const contextValue = useMemo(
    () => ({ user, saveUser, clearUser, isLoggedIn, allowedRoutes }),
    [user, saveUser, clearUser, isLoggedIn, allowedRoutes]
  )

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
}
